import { useCallback } from "react";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { selectCurrentUser } from "features/auth/auth-slice";
import { useCreateJwtTokenMutation } from "services/suno-comms/suno-comms-api";
import type { JWTTokenResponse } from "services/suno-comms/types/suno-comms-auth.type";

import { selectCommsAuthorized, setAuthTokenInfo } from "./leads-ui.slice";

export type CommsTokenInfo = {
  access_token: string;
  refresh_token: string;
  access_token_expires_at: string;
};

// What the leads slice keeps from a JWT create, or null when the response
// carries no usable token (an error, or a 2xx without one). Shared by
// `LeadsProvider` and `useEnsureCommsSession` so the two never drift.
export function commsTokenInfo(
  response: { data?: JWTTokenResponse } | { error: unknown }
): CommsTokenInfo | null {
  if ("error" in response || !response.data?.access_token) return null;
  return {
    access_token: response.data.access_token,
    refresh_token: response.data.refresh_token,
    access_token_expires_at: response.data.expires_at
  };
}

// A comms call from outside /leads (where `LeadsProvider` is not mounted)
// needs a JWT in the leads slice first — the comms base query reads it from
// there. Resolves true once a live token is in the slice, false when this
// user cannot get one; the caller then shows its generic error.
export default function useEnsureCommsSession(): () => Promise<boolean> {
  const dispatch = useAppDispatch();
  const authorized = useAppSelector(selectCommsAuthorized);
  const apiKey = useAppSelector(selectCurrentUser)?.comms_api_key;
  const [createJwtToken] = useCreateJwtTokenMutation();

  return useCallback(async () => {
    if (authorized) return true;
    if (!apiKey) return false;
    const info = commsTokenInfo(await createJwtToken(String(apiKey)));
    if (!info) return false;
    dispatch(setAuthTokenInfo(info));
    return true;
  }, [apiKey, authorized, createJwtToken, dispatch]);
}
